import { useCallback } from "react";

export type RecaptchaMode = "score" | "policy-based";

const RECAPTCHA_LOAD_RETRY_INTERVAL_MS = 100;
const RECAPTCHA_LOAD_MAX_RETRIES = 30; // 최대 3초 대기

// grecaptcha.enterprise.execute() 는 동시에 여러 번 호출되면 에러가 발생하므로,
// 한 페이지 내 여러 컴포넌트가 각자 useRecaptcha() 를 호출하더라도
// execute() 만큼은 모듈 스코프의 대기줄(배열)에 넣고 한 번에 하나씩만 순서대로 처리한다.
type ExecuteTask = {
  run: () => PromiseLike<string>;
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
};

const executeTaskQueue: ExecuteTask[] = [];
let isProcessingExecuteQueue = false;

// 이미 처리 루프가 돌고 있으면 그냥 반환한다.
// 그 루프가 while 문에서 대기줄을 계속 확인하므로, 새로 push된 작업도 알아서 처리된다.
const processExecuteQueue = async (): Promise<void> => {
  if (isProcessingExecuteQueue) {
    console.debug("ReCAPTCHA execute queue is already being processed");
    return;
  }
  console.debug("Starting to process reCAPTCHA execute queue");
  isProcessingExecuteQueue = true;

  // try/finally 로 감싸서, 루프 안에서 예상치 못한 예외가 새더라도
  // isProcessingExecuteQueue 가 true 로 영구히 남아 대기줄이 멈추지 않게 한다.
  try {
    while (executeTaskQueue.length > 0) {
      const task = executeTaskQueue.shift()!; // 대기줄 맨 앞 작업을 하나 꺼낸다

      try {
        const token = await task.run(); // 실제 execute() 호출이 끝날 때까지 여기서 기다림
        console.debug("ReCAPTCHA execute task completed successfully");
        task.resolve(token);
      } catch (error) {
        console.debug("ReCAPTCHA execute task failed", error);
        task.reject(error); // 이 작업만 실패 처리하고, 대기줄은 계속 진행
      }
    }
  } finally {
    isProcessingExecuteQueue = false;
  }
};

const enqueueExecute = (run: () => PromiseLike<string>): Promise<string> => {
  return new Promise<string>((resolve, reject) => {
    executeTaskQueue.push({ run, resolve, reject });
    console.debug("Enqueued reCAPTCHA execute task. Queue length:", executeTaskQueue.length);
    processExecuteQueue().catch((error) => console.debug("ReCAPTCHA execute queue processing failed unexpectedly", error));
  });
};

/**
 * reCAPTCHA Enterprise 사이트키 조회
 * - score: 위험 분석 점수 기반 (기존 방식)
 * - policy-based: policy-based challenge 방식 (필요 시 챌린지 노출)
 *
 * MetaTag(스크립트 로드)와 useRecaptcha(토큰 발급)가 동일한 키를 사용하도록 공유한다.
 */
export const getRecaptchaSiteKey = (mode: RecaptchaMode): string | undefined => {
  if (mode === "policy-based") {
    return process.env.REACT_APP_RE_CAPTCHA_POLICY_BASED_CHALLENGE_SITE_KEY;
  }
  return process.env.REACT_APP_RE_CAPTCHA_SITE_KEY;
};

const waitForRecaptchaEnterprise = async (retries = RECAPTCHA_LOAD_MAX_RETRIES): Promise<void> => {
  if (typeof window !== "undefined" && window.grecaptcha?.enterprise) {
    return;
  }
  if (retries <= 0) {
    throw new Error("reCAPTCHA enterprise script is not loaded");
  }
  await new Promise((resolve) => setTimeout(resolve, RECAPTCHA_LOAD_RETRY_INTERVAL_MS));
  return waitForRecaptchaEnterprise(retries - 1);
};

/**
 * reCAPTCHA Enterprise 토큰 발급 훅
 *
 * 사용 화면에서는 동일한 mode 로 MetaTag 스크립트가 로드되어 있어야 한다.
 * ex) <MetaTag isReCAPTCHA reCaptchaMode="policy-based" />
 */
export const useRecaptcha = (option?: { mode?: RecaptchaMode }) => {
  const { mode = "score" } = option || {};
  const siteKey = getRecaptchaSiteKey(mode);

  const getToken = useCallback(
    async (action: string): Promise<string> => {
      if (!siteKey) {
        throw new Error(`reCAPTCHA site key is not configured (mode: ${mode})`);
      }
      await waitForRecaptchaEnterprise();
      await new Promise<void>((resolve) => window.grecaptcha.enterprise.ready(resolve));
      return enqueueExecute(() => window.grecaptcha.enterprise.execute(siteKey, { action }));
    },
    [siteKey, mode],
  );

  return { getToken, siteKey };
};
